/**
 * useBillableAccounts — the billable accounts (subscriptions) the caller can
 * charge to, for the header account switcher.
 *
 * Server state via TanStack Query, keyed `["billable-accounts"]`. Each account
 * carries its own live balance. Freshness mirrors useCreditBalance:
 *   1. Any credit activity WebSocket event (consumed in useUnifiedEventConsumer)
 *      invalidates this query so every visible account row refetches. The WS
 *      payload has no subscription_id, so the whole list is blanket-invalidated.
 *   2. A 60s `refetchInterval` backstop guarantees eventual correctness even
 *      if an event is missed (dropped socket, reconnect gap).
 *
 * `useSetActiveBillingAccount` switches the account charges route to. On success
 * it invalidates the account list plus the balance / billing-context / my-
 * subscription queries so the whole billing surface reflects the new default.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBillableAccounts,
  setActiveBillingAccount,
  type BillableAccount,
} from "../services/billingApi";
import { getResponseStatus } from "../utils/errorUtils";

export const BILLABLE_ACCOUNTS_QUERY_KEY = ["billable-accounts"] as const;

const STALE_MS = 60_000;
const REFETCH_MS = 60_000;

export function useBillableAccounts() {
  const query = useQuery<BillableAccount[]>({
    queryKey: BILLABLE_ACCOUNTS_QUERY_KEY,
    queryFn: getBillableAccounts,
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });

  const accounts = query.data ?? [];
  const activeAccount =
    accounts.find((a) => a.isDefault) ?? accounts[0] ?? null;

  return {
    accounts,
    activeAccount,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Mutation to set the active billing account. `null` clears the explicit
 * choice (server falls back to the default). On success, invalidates every
 * query whose result depends on the active account.
 */
export function useSetActiveBillingAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subscriptionId: string | null) =>
      setActiveBillingAccount(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLABLE_ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["credit-balance"] });
      queryClient.invalidateQueries({ queryKey: ["billing-contexts"] });
      queryClient.invalidateQueries({ queryKey: ["my-subscription"] });
    },
  });
}
